/**
 * Sitemap Generator Utility
 * 
 * This utility helps generate a sitemap.xml file for better search engine indexing.
 * It includes all public-facing routes and dynamic routes based on data from the API.
 */

import axios from 'axios';

// Base URL from environment variable
const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://teemwildlifewonders.com';

/**
 * Fetches all tours from the API to include in the sitemap
 * @returns {Promise<Array>} Array of tour slugs
 */
export const fetchTourSlugs = async () => {
  try {
    const response = await axios.get('/api/v1/tours/all-slugs');
    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching tour slugs for sitemap:', error);
    return [];
  }
};

/**
 * Fetches all blog post slugs from the API to include in the sitemap
 * @returns {Promise<Array>} Array of blog post slugs
 */
export const fetchBlogSlugs = async () => {
  try {
    const response = await axios.get('/api/v1/blog/all-slugs');
    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching blog slugs for sitemap:', error);
    return [];
  }
};

/**
 * Fetches all category slugs from the API to include in the sitemap
 * @returns {Promise<Array>} Array of category slugs
 */
export const fetchCategorySlugs = async () => {
  try {
    const response = await axios.get('/api/v1/categories/all-slugs');
    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching category slugs for sitemap:', error);
    return [];
  }
};

/**
 * Generates a sitemap XML string with all site URLs
 * @returns {Promise<string>} XML string for the sitemap
 */
export const generateSitemap = async () => {
  // Static routes that are always in the sitemap
  const staticRoutes = [
    '',                // Home page
    '/tours',          // Tours listing
    '/about',          // About page
    '/contact',        // Contact page
    '/blog',           // Blog listing
    '/login',          // Login page
    '/register',       // Register page
  ];

  // Get dynamic routes from API
  const [tourSlugs, blogSlugs, categorySlugs] = await Promise.all([
    fetchTourSlugs(),
    fetchBlogSlugs(),
    fetchCategorySlugs()
  ]);

  // Current date for lastmod
  const today = new Date().toISOString().split('T')[0];

  // XML header
  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  xml += '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';

  // Add static routes
  staticRoutes.forEach(route => {
    xml += '  <url>\n';
    xml += `    <loc>${SITE_URL}${route}</loc>\n`;
    xml += `    <lastmod>${today}</lastmod>\n`;
    xml += '    <changefreq>weekly</changefreq>\n';
    xml += '    <priority>0.8</priority>\n';
    xml += '  </url>\n';
  });

  // Add tour detail pages
  tourSlugs.forEach(slug => {
    xml += '  <url>\n';
    xml += `    <loc>${SITE_URL}/tours/${slug}</loc>\n`;
    xml += `    <lastmod>${today}</lastmod>\n`;
    xml += '    <changefreq>weekly</changefreq>\n';
    xml += '    <priority>0.7</priority>\n';
    xml += '  </url>\n';
  });

  // Add blog post pages
  blogSlugs.forEach(slug => {
    xml += '  <url>\n';
    xml += `    <loc>${SITE_URL}/blog/${slug}</loc>\n`;
    xml += `    <lastmod>${today}</lastmod>\n`;
    xml += '    <changefreq>monthly</changefreq>\n';
    xml += '    <priority>0.6</priority>\n';
    xml += '  </url>\n';
  });

  // Add category pages
  categorySlugs.forEach(slug => {
    xml += '  <url>\n';
    xml += `    <loc>${SITE_URL}/tours/category/${slug}</loc>\n`;
    xml += `    <lastmod>${today}</lastmod>\n`;
    xml += '    <changefreq>weekly</changefreq>\n';
    xml += '    <priority>0.7</priority>\n';
    xml += '  </url>\n';
  });

  // Close XML
  xml += '</urlset>';
  
  return xml;
};

/**
 * Generates a robots.txt content
 * @returns {string} Content for robots.txt file
 */
export const generateRobotsTxt = () => {
  return `# robots.txt for ${SITE_URL}
User-agent: *
Allow: /

# Sitemaps
Sitemap: ${SITE_URL}/sitemap.xml

# Disallow admin and private areas
Disallow: /admin/
Disallow: /dashboard/
`;
};

export default {
  generateSitemap,
  generateRobotsTxt
};
